import axios from "axios";
import { Call, BaseCall } from "../types/features/Call";
import { PaginatedResponse } from "../types/core/PaginatedResponse";
import { MAIN_API_URL } from "../config/api";

const apiClient = axios.create({
  baseURL: MAIN_API_URL,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
    }
    return Promise.reject(error);
  }
);

export const callService = {
  // ============================================
  // APPELS CLASSIQUES (CRUD + STATS)
  // ============================================

  async getCalls(page: number = 1, perPage: number = 20): Promise<PaginatedResponse<Call>> {
    const response = await apiClient.get<PaginatedResponse<Call>>('/calls', {
      params: { page, per_page: perPage },
    });
    return response.data;
  },

  async getCallById(id: string): Promise<Call | null> {
    const response = await apiClient.get<Call>(`/calls/${id}`);
    return response.data;
  },

  async getCallsByClient(clientId: string, page: number = 1, perPage: number = 20): Promise<PaginatedResponse<Call>> {
    const response = await apiClient.get<PaginatedResponse<Call>>('/calls', {
      params: { client_id: clientId, page, per_page: perPage },
    });
    return response.data;
  },

  async createCall(call: Omit<Call, "id" | "created_at" | "updated_at">): Promise<Call> {
    const response = await apiClient.post<Call>('/calls', call);
    return response.data;
  },

  async updateCall(id: string, data: Partial<BaseCall>): Promise<Call> {
    const response = await apiClient.put<Call>(`/calls/${id}`, data);
    return response.data;
  },

  async deleteCall(id: string): Promise<void> {
    await apiClient.delete(`/calls/${id}`);
  },

  async getStats(agentId?: string): Promise<CallStats> {
    const response = await apiClient.get<CallStats>('/calls/stats', {
      params: { agent_id: agentId },
    });
    return response.data;
  },

  async getCallAudioStream(callId: string): Promise<string> {
    try {
      const response = await apiClient.get(`/calls/${callId}/audio`, { responseType: 'blob' });
      return URL.createObjectURL(response.data);
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        const errorText = await error.response.data.text();
        console.error("Erreur Laravel brute :", JSON.parse(errorText));
      }
      throw error;
    }
  },

  async getCallAudioUrl(callId: string): Promise<{ audio_url: string }> {
    const response = await apiClient.get<{ audio_url: string }>(`/calls/${callId}/audio-url`);
    return response.data;
  },

  // ============================================
  // TWILIO TOKENS
  // ============================================

  /**
   * Génère un token Twilio pour un agent (softphone dans le dashboard)
   */
  async getTwilioToken(): Promise<{ token: string; identity: string }> {
    const response = await apiClient.get<{ token: string; identity: string }>('/calls/token');
    return response.data;
  },

  /**
   * Génère un token Twilio pour un client (appel WebRTC depuis le site)
   */
  async getWebRTCToken(entrepriseId: string, email: string): Promise<{
    success: boolean;
    data: {
      client_id: string;
      identity: string;
      token: string;
    };
  }> {
    const response = await apiClient.post('/webrtc/token', {
      entreprise_id: entrepriseId,
      email: email
    });
    return response.data;
  },
};

export interface CallStats {
  total: number;
  entrants: number;
  sortants: number;
  duree_moyenne: number;
}